import { useQuery } from "@tanstack/react-query";
import type { PersonIncentive } from "@/lib/person-incentive";
import { personKeys } from "@/lib/query";

/**
 * The signed-in person's incentive scorecard, proxied from the Nymbl API Gateway
 * by `GET /api/persons/:personId/incentive`.
 *
 * Self only — the server answers 403 to anyone else, so the Incentives tab is
 * only offered on your own profile. A live read of NOS on every view, so it gets
 * a long `staleTime` and no refetch on window focus: the figures change on NOS's
 * schedule, and each fetch spends a rate-limited gateway call.
 */
export function usePersonIncentive(personId: string) {
  return useQuery({
    queryKey: personKeys.incentive(personId),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    queryFn: async (): Promise<PersonIncentive> => {
      const res = await fetch(`/api/persons/${personId}/incentive`);
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        const message =
          body !== null && typeof body === "object" && "error" in body
            ? String(body.error)
            : "Could not load your incentive scorecard";
        throw new Error(message);
      }
      const json = await res.json();
      return json.data;
    },
  });
}
